import React from 'react';
import { observer } from 'mobx-react';
import { CyClass } from '../../../../../common/cy-classes';
import { OrbitComponent } from '../../../base-components/orbit-component/orbit-component';
import { IVideoPlayer } from '../../../../services/video-live-stream/video-players/video-player.interface';
import { action, observable, makeObservable } from 'mobx';
import { VideoBox } from './boxes/video.box';
import { VideoPlayButtonBox } from "../video-play-button/video-play-button.box";
import {GameInfo} from "../../../../services/games/game-info";
import {VideoContainerBox} from '../video-container/video-container.box';
import {IVideoPlayingService} from "../../../../services/video-live-stream/video-playing.service.interface";

interface VideoComponentProps {
    gameInfo: GameInfo;
    muted: boolean;
    onError?: (hasError: boolean, retryFn?: (() => void) | undefined) => void;
    onLoading?: (isLoading: boolean) => void;
    onWatchTimeLimitReached?: (watchTimeLimitReached: boolean) => void;
    isInCenterStage: boolean;
    inViewport: boolean;
    display: boolean;
    videoPlayingService: IVideoPlayingService;
    isLastGametype?: boolean;
}

@observer
export class VideoComponent extends OrbitComponent<VideoComponentProps> {
    @observable.ref
    private _videoPlayer: IVideoPlayer = null;

    constructor(props: VideoComponentProps) {
        super(props);

        makeObservable(this);
    }

    @action.bound
    setLiveStreamPlayer(player: IVideoPlayer) {
        this._videoPlayer = player;
    }

    private _videoElement: HTMLVideoElement | null = null;
    private _isAppVisible = true;

    private _captureVideoRef = (mediaElement: HTMLVideoElement | null) => {
        if (mediaElement) {
            this._videoElement = mediaElement;
        }
    }

    private async _createVideoPlayer() {
        const liveStreamPlayer = await this.props.videoPlayingService.getVideoPlayer(this.props.gameInfo, this._videoElement, this.props.isLastGametype);
        this.setLiveStreamPlayer(liveStreamPlayer);
    }

    private _playVideo = async () => {
        if (!this._videoPlayer) {
            await this._createVideoPlayer();
        }

        this._videoPlayer.play();
    }

    private _pauseVideo = () => {
        if (this._videoPlayer) {
            this._videoPlayer.pause();
        }
    }

    private _detectIfVideoIsInFocus = () => {
        if (this._isAppVisible && this.props.isInCenterStage && this.props.inViewport) {
            this._playVideo();
        } else {
            this._pauseVideo();
        }
    }

    private _handlePlayVideoClick = () => {
        if (this._isAppVisible && this.props.isInCenterStage && this.props.inViewport) {
            this._playVideo();
            this._videoPlayer.logUIAction('info', 'VideoPlayClick');
            const gaCategory = this.props.videoPlayingService.gaCategory;
            this.services.analytics.sendAnalytics(gaCategory, "AUTOPLAY", "VIDEO_PLAY_CLICK", this.props.gameInfo.gameType, true);
        }
    }

    private _renderPlayerControls = () => {
        return this._videoPlayer?.autoplayAllowed ? null : (
            <VideoPlayButtonBox onClick={this._handlePlayVideoClick}/>
        );
    }

    @action.bound
    private _destroyPlayer = () => {
        if (this._videoPlayer) {
            this._videoPlayer.destroy();
            this._videoPlayer = null;
        }
    };

    private _retryPlay = () => {
        this._destroyPlayer();
        this._detectIfVideoIsInFocus();
    };

    componentDidMount() {
        super.componentDidMount();

        this._detectIfVideoIsInFocus();

        this.reaction(() => this._videoPlayer?.hasError, (hasError) => {
            this.props.onError(hasError, hasError ? this._retryPlay : undefined);
        });
        this.reaction(() => this._videoPlayer?.isLoading, (isLoading) => {
            this.props.onLoading(isLoading);
        });
        this.reaction(() => this._videoPlayer?.watchTimeLimitReached, (watchTimeLimitReached) => {
            this.props.onWatchTimeLimitReached(watchTimeLimitReached);
        });
        this.reaction(() => this.props.videoPlayingService.isAppVisible, (isAppVisible) => {
            this._isAppVisible = isAppVisible;
            this._detectIfVideoIsInFocus();
        });
    }

    componentDidUpdate(prevProps: VideoComponentProps) {
        if (this.props.isInCenterStage !== prevProps.isInCenterStage || this.props.inViewport !== prevProps.inViewport) {
            this._detectIfVideoIsInFocus();
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        this._destroyPlayer();
    }

    render() {
        return (
            <VideoContainerBox
                className={CyClass('cy-live-video-item')}
                videoIsLoading={this._videoPlayer?.isLoading}
                videoWatchTimeLimitReached={this._videoPlayer?.watchTimeLimitReached}
                displayVideo={this.props.display}
            >
                <VideoBox
                    ref={this._captureVideoRef}
                    playsInline={true}
                    muted={this.props.muted}
                />
                {this._renderPlayerControls()}
            </VideoContainerBox>
        )
    }
}
